import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import {
  recurringScopeLabelFallbacks,
  recurringScopeLabelKeys,
} from '@common-views/calendar/recurring-dialog-options';

/**
 * Confirmation for the recurring-scope questions (edit / delete this occurrence,
 * this and following, all) and for plain delete confirmations.
 *
 * The choice IS the content here, so the options are the largest thing in the
 * dialog: full-width rows that are themselves the click target, with the
 * selected one carrying the brand. The previous treatment led with a gradient
 * badge and gave both buttons shimmer sweeps and hover lifts, which read as
 * decoration around a question rather than as the question.
 *
 * The values handed back through onClose are unchanged (they are a wire format —
 * see recurring-dialog-options.ts); only the LABELS the user reads are mapped.
 * Cancel still takes focus.
 *
 * `destructive` marks an action that cannot be undone: the confirm button turns
 * red and, unless `confirmLabel` says otherwise, names the verb (Delete) instead
 * of the meaningless "OK". `description` now renders ABOVE the options too — it
 * used to be dropped entirely whenever options were present, which is exactly
 * the case where a warning matters most.
 */
export const MessageConfirmationDialog = <T extends string>({
  title,
  onClose,
  options,
  selected,
  shouldShowCancelButton = true,
  description,
  destructive = false,
  confirmLabel,
}: {
  title: string;
  onClose: (value?: T) => void;
  options?: T[];
  selected?: number;
  shouldShowCancelButton?: boolean;
  /** A string in every existing caller. Widened to a node so a caller that has
   *  to NAME the thing it is about to destroy can hand over a small block —
   *  when / to whom / what it says — instead of grammar-in-code cramming three
   *  facts into one interpolated sentence, which does not survive translation
   *  or an RTL run. */
  description?: React.ReactNode;
  /** The action cannot be undone — paints the confirm button red. */
  destructive?: boolean;
  /** Overrides the confirm button text (default: Delete / OK). */
  confirmLabel?: string;
}) => {
  const { t } = useTranslation(namespace);
  const [value, setValue] = useState<T>(options?.[selected || 0] || '' as T);

  // The recurring scope options carry model-speak values ("All messages").
  // Show the outcome instead; anything unmapped renders as-is.
  const labelForOption = (option: T): string => {
    const key = (recurringScopeLabelKeys as Record<string, string>)[option];
    if (!key) return option;
    return t(key, (recurringScopeLabelFallbacks as Record<string, string>)[option]);
  };

  const handleCancel = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    onClose();
  };

  const handleOk = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    onClose(value);
  };

  const optionRowStyle = (isSelected: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '11px 12px',
    borderRadius: '10px',
    border: `1px solid ${isSelected ? 'var(--cta-button-bg, #2B7FFF)' : 'var(--tasks-cell-border)'}`,
    background: isSelected ? 'var(--primary-bg, rgba(43,127,255,0.08))' : 'transparent',
    color: 'var(--color-text)',
    font: 'inherit',
    fontSize: '14px',
    fontWeight: isSelected ? 600 : 500,
    textAlign: 'start',
    cursor: 'pointer',
  });

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent
        className="max-w-[420px] min-w-0 sm:min-w-[340px] p-0"
        style={{
          backgroundColor: 'var(--color-surface)',
          backgroundImage: 'none',
          borderRadius: '16px',
          border: '1px solid var(--tasks-cell-border)',
          boxShadow: '0 24px 64px -20px var(--modal-shadow)',
        }}
      >
        <div onClick={(e) => e.stopPropagation()} style={{ padding: '22px 22px 18px' }}>
          <DialogHeader>
            <DialogTitle
              style={{
                color: 'var(--color-text)',
                fontSize: '17px',
                fontWeight: 700,
                letterSpacing: '-0.01em',
                textAlign: 'start',
                margin: 0,
                padding: 0,
              }}
            >
              {title}
            </DialogTitle>
          </DialogHeader>

          {(description || !options) && (
            <div
              style={{
                marginTop: '10px',
                /* --tasks-text-secondary measures 4.29:1 on the dialog's own
                   surface — under 4.5:1, on the sentence that says an action
                   cannot be undone. --color-text-muted is the same role a step
                   darker (7.06:1 light / 9.15:1 dark) and is what the rest of
                   this dialog's secondary copy already resolves to. */
                color: 'var(--color-text-muted)',
                fontSize: '14px',
                lineHeight: 1.5,
              }}
            >
              {description || t('scheduledMessage.confirmation.removeMessage')}
            </div>
          )}

          {options && (
            <div
              role="radiogroup"
              aria-label={title}
              style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: description ? '14px' : '16px' }}
            >
              {options.map((option) => {
                const isSelected = option === value;
                return (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setValue(option)}
                    style={optionRowStyle(isSelected)}
                  >
                    <span
                      aria-hidden
                      style={{
                        flex: 'none',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: `2px solid ${isSelected ? 'var(--cta-button-bg, #2B7FFF)' : 'var(--tasks-text-secondary)'}`,
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      {isSelected && (
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: 'var(--cta-button-bg, #2B7FFF)',
                            display: 'block',
                          }}
                        />
                      )}
                    </span>
                    {labelForOption(option)}
                  </button>
                );
              })}
            </div>
          )}

          <DialogFooter
            style={{
              padding: 0,
              marginTop: '20px',
              gap: '8px',
              display: 'flex',
              justifyContent: 'flex-end',
              flexDirection: 'row',
            }}
          >
            {shouldShowCancelButton && (
              <button
                autoFocus
                type="button"
                onClick={handleCancel}
                style={{
                  height: '38px',
                  padding: '0 16px',
                  borderRadius: '10px',
                  border: '1px solid var(--tasks-cell-border)',
                  background: 'transparent',
                  color: 'var(--color-text)',
                  font: 'inherit',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            )}

            <button
              type="button"
              onClick={handleOk}
              style={{
                height: '38px',
                padding: '0 18px',
                borderRadius: '10px',
                border: 'none',
                background: destructive ? 'var(--color-danger, #DC2626)' : 'var(--cta-button-bg, #2B7FFF)',
                color: '#fff',
                font: 'inherit',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {confirmLabel
                ?? (destructive || !options || options.length === 0
                  ? t('common.delete')
                  : t('common.ok'))}
            </button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
